import {caseInsensitiveComparator} from '@brunata-angular/util';
import {
    CustomizedExportChannel,
    CustomizedExportSuperTypeChannels,
    customizedExportChannelKey,
    meterChannelId,
    meterModelId,
    meterModelKey,
} from './channels.service';

export type ChannelLabel = {
    code: string;
    name: string | null;
};

export type MeterModelRow = {
    id: string;
    meterSuperTypeNo: string;
    superTypeNameKey: string;
    vendorName: string | null;
    modelName: string;
    channelCount: number;
    selected: boolean;
};

export type MeterChannelRow = {
    id: string;
    meterSuperTypeNo: string;
    superTypeNameKey: string;
    label: ChannelLabel;
    unitKey: string | null;
    accessibleName: string;
    offeringModelCount: number;
    modelCount: number;
    primaryModelCount: number;
    selected: boolean;
};

export type MeterSuperTypeOption = {
    meterSuperTypeNo: string;
    nameKey: string;
};

export type ChannelLessRow = MeterSuperTypeOption & {
    selected: boolean;
};

export type MeterScope = {
    loading: boolean;
    failed: boolean;
    offersNoChannels: boolean;
    models: MeterModelRow[];
    channels: MeterChannelRow[];
    channelLess: ChannelLessRow[];
    vendorNames: ReadonlyArray<string>;
    superTypes: ReadonlyArray<MeterSuperTypeOption>;
};

export type MeterScopeAvailability = {
    modelIds: string[];
    channelIds: string[];
    primaryChannelIds: string[];
};

export type MeterScopeChannel = {
    meterSuperTypeNo: string;
    modelConfigurationId: string | null;
    obisCode: string | null;
    channelKey: string | null;
};

export function channelLabel(channel: CustomizedExportChannel): ChannelLabel | null {
    const {visualizeCode, prettyName, obisCode} = channel;
    if (visualizeCode !== null) {
        return {code: visualizeCode, name: prettyName === visualizeCode ? null : prettyName};
    }
    if (prettyName !== null) {
        return {code: prettyName, name: null};
    }
    return obisCode === null ? null : {code: obisCode, name: null};
}

export function isDisplayable(channel: CustomizedExportChannel): boolean {
    return channelLabel(channel) !== null;
}

function superTypeNameKey(meterSuperTypeNo: string): string {
    return `mappers_meterType.${meterSuperTypeNo}`;
}

type OfferedChannel = {
    meterSuperTypeNo: string;
    channel: CustomizedExportChannel;
    label: ChannelLabel;
    modelId: string;
    modelKey: string;
    channelId: string;
};

function offeredChannels(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
    selectedSuperTypes: ReadonlyArray<string>,
): OfferedChannel[] {
    const wanted = new Set(selectedSuperTypes);
    return available
        .filter(group => wanted.has(group.meterSuperTypeNo))
        .flatMap(group =>
            group.channels.flatMap(channel => {
                const label = channelLabel(channel);
                return label === null
                    ? []
                    : [
                          {
                              meterSuperTypeNo: group.meterSuperTypeNo,
                              channel,
                              label,
                              modelId: meterModelId(group.meterSuperTypeNo, channel),
                              modelKey: meterModelKey(group.meterSuperTypeNo, channel),
                              channelId: meterChannelId(group.meterSuperTypeNo, channel),
                          },
                      ];
            }),
        );
}

export function buildMeterScope(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels> | null | undefined,
    selectedSuperTypes: ReadonlyArray<string>,
    selectedModelIds: ReadonlyArray<string>,
    selectedChannelIds: ReadonlyArray<string>,
    selectedChannelLess: Readonly<Record<string, boolean>> = {},
): MeterScope {
    const nothingOffered = {
        models: [],
        channels: [],
        channelLess: [],
        vendorNames: [],
        superTypes: [],
        offersNoChannels: false,
    };
    if (available === null) {
        return {...nothingOffered, loading: false, failed: true};
    }
    if (available === undefined) {
        return {...nothingOffered, loading: selectedSuperTypes.length > 0, failed: false};
    }

    const answeredSuperTypes = new Set(available.map(group => group.meterSuperTypeNo));
    if (selectedSuperTypes.some(superTypeNo => !answeredSuperTypes.has(superTypeNo))) {
        return {...nothingOffered, loading: true, failed: false};
    }

    const offered = offeredChannels(available, selectedSuperTypes);
    const pickedModels = new Set(selectedModelIds);
    const pickedChannels = new Set(selectedChannelIds);
    const modelsOfferingPickedChannels = modelsOffering(offered, pickedChannels);
    const offeredByShownModels = offered.filter(entry =>
        modelsOfferingPickedChannels.has(entry.modelId),
    );

    return {
        loading: false,
        failed: false,
        offersNoChannels: selectedSuperTypes.length > 0 && offered.length === 0,
        models: modelRows(offeredByShownModels, pickedModels),
        channels: channelRows(offered, pickedModels, pickedChannels),
        channelLess: channelLessRows(available, selectedSuperTypes, selectedChannelLess),
        vendorNames: vendorNames(offeredByShownModels),
        superTypes: superTypeOptions(offered),
    };
}

function channelLessRows(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
    selectedSuperTypes: ReadonlyArray<string>,
    selectedChannelLess: Readonly<Record<string, boolean>>,
): ChannelLessRow[] {
    return pickedChannelLessGroups(available, selectedSuperTypes)
        .filter(hasDisplayableChannels)
        .map(group => ({
            meterSuperTypeNo: group.meterSuperTypeNo,
            nameKey: superTypeNameKey(group.meterSuperTypeNo),
            selected: isChannelLessTicked(selectedChannelLess, group.meterSuperTypeNo),
        }));
}

function channelLessOnlySuperTypes(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
    selectedSuperTypes: ReadonlyArray<string>,
): string[] {
    return pickedChannelLessGroups(available, selectedSuperTypes)
        .filter(group => !hasDisplayableChannels(group))
        .map(group => group.meterSuperTypeNo);
}

function pickedChannelLessGroups(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
    selectedSuperTypes: ReadonlyArray<string>,
): CustomizedExportSuperTypeChannels[] {
    const wanted = new Set(selectedSuperTypes);
    return available.filter(
        group => wanted.has(group.meterSuperTypeNo) && group.hasChannelLessMeters === true,
    );
}

function hasDisplayableChannels(group: CustomizedExportSuperTypeChannels): boolean {
    return group.channels.some(isDisplayable);
}

export function selectedChannelLessSuperTypes(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels> | null | undefined,
    scope: MeterScope,
    selectedSuperTypes: ReadonlyArray<string>,
    selectedChannelLess: Readonly<Record<string, boolean>>,
): string[] {
    if (available == null || scope.loading || scope.failed) {
        return selectedSuperTypes.filter(superTypeNo => selectedChannelLess[superTypeNo] === true);
    }
    return [
        ...scope.channelLess.filter(row => row.selected).map(row => row.meterSuperTypeNo),
        ...channelLessOnlySuperTypes(available, selectedSuperTypes),
    ];
}

export function includedChannelLessSuperTypes(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels> | null | undefined,
    selectedSuperTypes: ReadonlyArray<string>,
    selectedChannelLess: Readonly<Record<string, boolean>>,
): string[] {
    const withoutRow = new Set(
        available == null ? [] : channelLessOnlySuperTypes(available, selectedSuperTypes),
    );
    return selectedSuperTypes.filter(
        superTypeNo =>
            withoutRow.has(superTypeNo) || isChannelLessTicked(selectedChannelLess, superTypeNo),
    );
}

export function isChannelLessTicked(
    selectedChannelLess: Readonly<Record<string, boolean>>,
    meterSuperTypeNo: string,
): boolean {
    return selectedChannelLess[meterSuperTypeNo] ?? true;
}

function modelRows(
    offered: ReadonlyArray<OfferedChannel>,
    pickedModels: ReadonlySet<string>,
): MeterModelRow[] {
    const rows = new Map<string, MeterModelRow>();
    for (const entry of offered) {
        const existing = rows.get(entry.modelId);
        if (existing === undefined) {
            rows.set(entry.modelId, {
                id: entry.modelId,
                meterSuperTypeNo: entry.meterSuperTypeNo,
                superTypeNameKey: superTypeNameKey(entry.meterSuperTypeNo),
                vendorName: entry.channel.vendorName ?? null,
                modelName: entry.channel.model,
                channelCount: 1,
                selected: pickedModels.has(entry.modelId),
            });
        } else {
            existing.channelCount += 1;
        }
    }
    return [...rows.values()];
}

function modelCountsPerSuperType(offered: ReadonlyArray<OfferedChannel>): Map<string, number> {
    const modelsPerSuperType = new Map<string, Set<string>>();
    for (const entry of offered) {
        const models = modelsPerSuperType.get(entry.meterSuperTypeNo) ?? new Set<string>();
        models.add(entry.modelId);
        modelsPerSuperType.set(entry.meterSuperTypeNo, models);
    }
    return new Map(
        [...modelsPerSuperType].map(([meterSuperTypeNo, models]) => [
            meterSuperTypeNo,
            models.size,
        ]),
    );
}

function modelsOffering(
    offered: ReadonlyArray<OfferedChannel>,
    channelIds: ReadonlySet<string>,
): Set<string> {
    return new Set(
        offered.filter(entry => channelIds.has(entry.channelId)).map(entry => entry.modelId),
    );
}

function channelRows(
    offered: ReadonlyArray<OfferedChannel>,
    pickedModels: ReadonlySet<string>,
    pickedChannels: ReadonlySet<string>,
): MeterChannelRow[] {
    const modelCounts = modelCountsPerSuperType(offered);
    const rows = new Map<string, MeterChannelRow>();
    const modelsCountedPerChannel = new Map<string, Set<string>>();

    for (const entry of offered) {
        let row = rows.get(entry.channelId);
        if (row === undefined) {
            row = {
                id: entry.channelId,
                meterSuperTypeNo: entry.meterSuperTypeNo,
                superTypeNameKey: superTypeNameKey(entry.meterSuperTypeNo),
                label: entry.label,
                unitKey:
                    entry.channel.unit === null
                        ? null
                        : `mappers_measurementUnit.${entry.channel.unit}`,
                accessibleName:
                    entry.label.name === null
                        ? entry.label.code
                        : `${entry.label.code} - ${entry.label.name}`,
                offeringModelCount: 0,
                modelCount: modelCounts.get(entry.meterSuperTypeNo) ?? 0,
                primaryModelCount: 0,
                selected: pickedChannels.has(entry.channelId),
            };
            rows.set(entry.channelId, row);
        }
        const counted = modelsCountedPerChannel.get(entry.channelId) ?? new Set<string>();
        if (!pickedModels.has(entry.modelId) || counted.has(entry.modelId)) {
            continue;
        }
        counted.add(entry.modelId);
        modelsCountedPerChannel.set(entry.channelId, counted);
        row.offeringModelCount += 1;
        if (entry.channel.primary) {
            row.primaryModelCount += 1;
        }
    }
    return [...rows.values()];
}

function vendorNames(offered: ReadonlyArray<OfferedChannel>): string[] {
    const names = new Set<string>();
    for (const entry of offered) {
        const vendorName = entry.channel.vendorName;
        if (vendorName != null && vendorName !== '') {
            names.add(vendorName);
        }
    }
    return [...names].sort(caseInsensitiveComparator(name => name));
}

function superTypeOptions(offered: ReadonlyArray<OfferedChannel>): MeterSuperTypeOption[] {
    const options = new Map<string, MeterSuperTypeOption>();
    for (const entry of offered) {
        options.set(entry.meterSuperTypeNo, {
            meterSuperTypeNo: entry.meterSuperTypeNo,
            nameKey: superTypeNameKey(entry.meterSuperTypeNo),
        });
    }
    return [...options.values()];
}

export function meterScopeAvailability(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
    selectedSuperTypes: ReadonlyArray<string>,
): MeterScopeAvailability {
    const offered = offeredChannels(available, selectedSuperTypes);
    const modelIds = new Set<string>();
    const channelIds = new Set<string>();
    const primaryChannelIds = new Set<string>();
    for (const entry of offered) {
        modelIds.add(entry.modelId);
        channelIds.add(entry.channelId);
        if (entry.channel.primary) {
            primaryChannelIds.add(entry.channelId);
        }
    }
    return {
        modelIds: [...modelIds],
        channelIds: [...channelIds],
        primaryChannelIds: [...primaryChannelIds],
    };
}

export function modelIdsForKeys(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
    selectedSuperTypes: ReadonlyArray<string>,
    modelKeys: ReadonlyArray<string>,
): string[] {
    const wanted = new Set(modelKeys);
    const modelIds = new Set<string>();
    for (const entry of offeredChannels(available, selectedSuperTypes)) {
        if (wanted.has(entry.modelKey)) {
            modelIds.add(entry.modelId);
        }
    }
    return [...modelIds];
}

function keyedMeterScopeChannel(entry: OfferedChannel): MeterScopeChannel {
    return {
        meterSuperTypeNo: entry.meterSuperTypeNo,
        modelConfigurationId: null,
        obisCode: null,
        channelKey: customizedExportChannelKey(entry.channel),
    };
}

function meterScopeChannel(entry: OfferedChannel): MeterScopeChannel {
    const {modelConfigurationId, obisCode} = entry.channel;
    return modelConfigurationId == null || obisCode === null
        ? keyedMeterScopeChannel(entry)
        : {
              meterSuperTypeNo: entry.meterSuperTypeNo,
              modelConfigurationId,
              obisCode,
              channelKey: null,
          };
}

function meterScopeChannelIdentity(channel: MeterScopeChannel): string {
    return JSON.stringify([
        channel.meterSuperTypeNo,
        channel.modelConfigurationId,
        channel.obisCode?.toUpperCase() ?? null,
        channel.channelKey,
    ]);
}

export function meterScopeChannels(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels> | null | undefined,
    selectedSuperTypes: ReadonlyArray<string>,
    selectedModelIds: ReadonlyArray<string>,
    selectedChannelIds: ReadonlyArray<string>,
): MeterScopeChannel[] {
    if (available == null) {
        return [];
    }
    const pickedModels = new Set(selectedModelIds);
    const pickedChannels = new Set(selectedChannelIds);
    const seen = new Set<string>();
    const channels: MeterScopeChannel[] = [];
    for (const entry of offeredChannels(available, selectedSuperTypes)) {
        if (!pickedModels.has(entry.modelId) || !pickedChannels.has(entry.channelId)) {
            continue;
        }
        const channel = meterScopeChannel(entry);
        const identity = meterScopeChannelIdentity(channel);
        if (seen.has(identity)) {
            continue;
        }
        seen.add(identity);
        channels.push(channel);
    }
    return channels;
}

export function meterScopeSelectionFromChannels(
    available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
    selectedSuperTypes: ReadonlyArray<string>,
    storedChannels: ReadonlyArray<MeterScopeChannel>,
): {modelIds: string[]; channelIds: string[]} {
    const stored = new Set(storedChannels.map(meterScopeChannelIdentity));
    const offered = offeredChannels(available, selectedSuperTypes);
    const modelIds = new Set<string>();
    const channelIds = new Set<string>();
    for (const entry of offered) {
        const identities = [meterScopeChannel(entry), keyedMeterScopeChannel(entry)].map(
            meterScopeChannelIdentity,
        );
        if (identities.some(identity => stored.has(identity))) {
            modelIds.add(entry.modelId);
            channelIds.add(entry.channelId);
        }
    }
    const modelsOfferingRestoredChannels = modelsOffering(offered, channelIds);
    for (const entry of offered) {
        if (!modelsOfferingRestoredChannels.has(entry.modelId)) {
            modelIds.add(entry.modelId);
        }
    }
    return {modelIds: [...modelIds], channelIds: [...channelIds]};
}
